import { BaseComponent } from "../../base-component/base-component";
import { DocumentType } from '../interfaces/product-card.interface';
import { ProductCardData } from "../product-card-display";
import { customElement, property } from "lit/decorators.js";
import { formattedJoin, isTextContainsHTMLTags, ordinalSuffixOf, stripHTMLTags } from "../../../common/utils/util";
import { generateProductCardMetadataIdentifiersText } from '../utils/product-card-metadata.util';
import { getProgramUrl } from '../utils/product-card.util';
import { html, nothing } from "lit";
import { booleanConverter } from "../../../common/converters";

@customElement('mhecomm-product-card-metadata-display')
export class ProductCardMetadataDisplay extends BaseComponent {
  @property({ type: String })
  descriptionFontSize? = 'fs-small';

  @property({ type: Object })
  product?: ProductCardData;

  @property({ type: String })
  programId;

  @property({ type: String })
  programTitle;

  @property({ type: String })
  businessLabelText;
  
  @property({ type: Boolean })
  showBusinessLabelType;

  @property({ type: String })
  businessLabelType;

  @property({ type: Boolean })
  showBusinessUnitLabel;

  @property({ converter: booleanConverter })
  wholeCardClickable = false;

  @property({ converter: booleanConverter })
  titleTabbable = true;

  @property({ type: Boolean })
  showProgramFamily = false;

  @property({ type: Boolean })
  showEdition = false;

  @property({ type: Boolean })
  showAuthors = false;

  @property({ type: Boolean })
  showISBN = false;

  @property({ type: Boolean })
  showMHID = false;

  @property({ type: Boolean })
  showGrades = false;

  @property({ type: Boolean })
  showCopyright = false;

  @property({ type: Boolean })
  showDescription = false;

  @property({ type: Boolean })
  disablePadding;

  @property({ type: String })
  titleWeight? = 'bold';

  @property()
  titleClickListener: ((e: Event) => void) | undefined;

  @property()
  programFamilyClickListener: ((e: Event) => void) | undefined;

  @property({ type: String })
  titleHref: string | undefined;

  @property({ type: String })
  headingLevel = "h3";

  private identifier;

  /**
   * Remove shadow DOM
   * @returns 
   */
  createRenderRoot() {
    return this;
  }
  
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    this.identifier = this.getIdentifier(this.product);
  }

  renderComp() {
    return html`
      <div id="card-main-metadata-${this.identifier}" class="lh-1 mb-3">
        ${this.generateBusinessLabelType(this.businessLabelText, this.businessLabelType, this.showBusinessUnitLabel, this.showBusinessLabelType)}
        ${this.generateProductCardTitle(this.product?.title!)}
        ${this.generateProductCardMetadataEditiontext(this.product?.edition)} 
        ${this.generateProductCardProgramFamily()} 
      </div>
      ${generateProductCardMetadataIdentifiersText(
        {
          [this.showMHID ? 'MHID' : 'ISBN']: this.product?.isbn, 
          ISBN13: this.product?.isbn13 
        },
        this.showMHID || this.showISBN
      )}
      ${this.generateProductCardMetadataAuthorsText(this.product?.contributors)}
      <div class="d-flex flex-row gap-2">
        ${this.generateProductCardMetadataGrades(this.product?.gradeLevels)}
        ${this.generateProductCardMetadataCopyrightText(this.product?.copyRightYear)}
      </div>
      ${this.generateProductCardDescription(this.product?.description)}`
  }

  generateBusinessLabelType(label, type, showLabel, showType) {
    return showLabel || showType
      ? html`
        <p class="mb-1 fs-small">
          ${showLabel ? html `<strong>${label}</strong>` : nothing} 
          ${showType ? type : nothing}
        </p>`
      : nothing
  }

  generateProductCardTitle(productTitle) {
    const headingLink = html`
      <a
        id="product-title-${this.identifier}-link"
        href="${this.titleHref!}"
        class="p-0 text-start fs-6 btn border-0 btn-link fw-${this.titleWeight} lh-sm product-card-title ${this.titleTabbable && this.wholeCardClickable ? 'stretched-link': ''}"
        @click="${this.titleClickListener}"
        tabindex="${this.titleTabbable ? '0' : '-1'}"
      >
        ${productTitle}
      </a>
  `;

  const headingClasses = "mb-1 lh-1 h5";
  const headingId = `product-title-${this.identifier}`;
    // WEA-1650 - need to review if we can use safely the html-static and usafeStatic here to avoid the switch case
    switch (this.headingLevel) {
      case 'h1':
        return html`<h1 id="${headingId}" class="${headingClasses}">${headingLink}</h1>`;
      case 'h2':
        return html`<h2 id="${headingId}" class="${headingClasses}">${headingLink}</h2>`;
      case 'h3':
        return html`<h3 id="${headingId}" class="${headingClasses}">${headingLink}</h3>`;
      case 'h4':
        return html`<h4 id="${headingId}" class="${headingClasses}">${headingLink}</h4>`;
      case 'h5':
        return html`<h5 id="${headingId}" class="${headingClasses}">${headingLink}</h5>`;
      case 'h6':
        return html`<h6 id="${headingId}" class="${headingClasses}">${headingLink}</h6>`;
      default:
        return html`<h3 id="${headingId}" class="${headingClasses}">${headingLink}</h3>`;
    }
  }

  generateProductCardProgramFamily() {
    if (!this.programId && !this.programTitle) return;
    return this.showProgramFamily && this.programId && this.programTitle
      ? html`<p id="program-family-${this.identifier}" class="fs-small"><em>Part of <a href="${getProgramUrl(this.programId)}" @click="${this.programFamilyClickListener}">${this.programTitle}</a></em></p>`
      : nothing
  }

  generateProductCardMetadataEditiontext(edition) {
    return this.showEdition && edition ? html`<p class="fs-small mb-1">${ordinalSuffixOf(edition)} Edition</p>` : nothing
  }

  generateProductCardMetadataAuthorsText(authors) {
    return this.showAuthors && authors ? html`<p class="fs-small mb-1"><strong> By: </strong> ${formattedJoin(authors)}</p>` : nothing
  }

  generateProductCardMetadataGrades(grades) {
    return this.showGrades && grades ? html`<p class="fs-small mb-1"><strong>Grades: </strong>${grades}</p>` : nothing;
  }

  generateProductCardMetadataCopyrightText(copyrightYear) {
    return this.showCopyright && copyrightYear
      ? html`
          <time class="mb-1 fs-small">
            <span>
              © ${copyrightYear}
            </span>
          </time>
        `
      : nothing
  }

  generateProductCardDescription(productDescription) {
    return this.showDescription && productDescription
      ? html `<p class="${this.descriptionFontSize} mb-1">${this.getStrippedText(productDescription)}</p>`
      : nothing
  }

  getStrippedText(string) {
    const strippedText =  isTextContainsHTMLTags(string) ? stripHTMLTags(string) : string;
    return this.limitString(strippedText, 300);
  }

  limitString(str, limit = 300) {
    if (str.length <= limit) {
      return str;
    } else {
      return str.substring(0, limit) + '...';
    }
  }

  getIdentifier(product?: ProductCardData): string {
    if (!product) {
      return '';
    }

    if (product.documentType === DocumentType.programs) {
      return product.programId ?? '';
    }

    return this.showMHID ? product.mhid ?? '' : product.isbn ?? '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-product-card-metadata-display': ProductCardMetadataDisplay;
  }
}
